import React, { useEffect, useMemo, useState } from 'react';
import WorkspacePage from '../components/layout/WorkspacePage';
import AdminReportingPanel from '../components/admin/AdminReportingPanel';
import DashboardErrorBoundary from '../components/DashboardErrorBoundary';
import Button from '../components/ui/Button';
import FormField from '../components/ui/FormField';
import Pill from '../components/ui/Pill';
import Select from '../components/ui/Select';
import DirectorDelegationsPanel from '../components/director/DirectorDelegationsPanel';
import DirectorKpisPanel from '../components/director/DirectorKpisPanel';
import DirectorLocationsPanel from '../components/director/DirectorLocationsPanel';
import DirectorModal from '../components/director/DirectorModal';
import DirectorUsersPanel from '../components/director/DirectorUsersPanel';
import DirectorPanelState from '../components/director/DirectorPanelState';
import StatusBanner from '../components/ui/StatusBanner';
import { useFetch } from '../hooks/useFetch';
import { useMutation } from '../hooks/useMutation';
import { useMultiFetch } from '../hooks/useMultiFetch';
import { apiRequest } from '../services/apiClient';
import { withQuery } from '../utils/buildQuery';
import { isPlatformWorkspaceUser, PLATFORM_COMPANY_SLUG } from '../utils/platformWorkspace';
import PlatformBillingTab from '../components/platform/PlatformBillingTab';
import DigestConfigPanel from '../components/platform/DigestConfigPanel';
import PlatformAttendanceSettingsPanel from '../components/platform/PlatformAttendanceSettingsPanel';
import PlatformIntegrationsTab from '../components/platform/PlatformIntegrationsTab';
import TenantConfigurationPanel from '../components/platform/TenantConfigurationPanel';
import UnauthorizedPage from './UnauthorizedPage';
import './RoleDashboards.css';

const ADMIN_TABS = [
  { key: 'home', label: 'Overview' },
  { key: 'users', label: 'Users & Roles' },
  { key: 'locations', label: 'Location Ownership' },
  { key: 'delegations', label: 'Delegations' },
  { key: 'kpis', label: 'KPI Configuration' },
  { key: 'reporting', label: 'Reporting & Export' },
  { key: 'tenant-config', label: 'Tenant Configuration' }
];

const PLATFORM_TABS = [
  { key: 'home', label: 'Platform Overview' },
  { key: 'companies', label: 'Companies' },
  { key: 'users', label: 'Platform Users' },
  { key: 'billing', label: 'Billing' },
  { key: 'tenant-config', label: 'Tenant Configuration' }
];

const COMPANY_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'active', label: 'Active' },
  { key: 'onboarding', label: 'Onboarding' },
  { key: 'healthy', label: 'Healthy' },
  { key: 'progress', label: 'In Progress' },
  { key: 'setup', label: 'Needs Setup' }
];

const COMPANY_SORT_OPTIONS = [
  { key: 'name', label: 'Name A-Z' },
  { key: 'health-desc', label: 'Health High → Low' },
  { key: 'health-asc', label: 'Health Low → High' },
  { key: 'users-desc', label: 'Users High → Low' },
  { key: 'locations-desc', label: 'Locations High → Low' }
];

function pathToTab(path) {
  const map = {
    '/admin-users': 'users',
    '/admin-locations': 'locations',
    '/admin-delegations': 'delegations',
    '/admin-kpis': 'kpis',
    '/admin-reporting': 'reporting',
    '/admin-tenant-configuration': 'tenant-config'
  };
  return map[path] || 'home';
}

function canEditTenantConfiguration(user) {
  const normalizedRole = String(user?.role || '').trim().toLowerCase();
  return ['director', 'admin', 'super_admin', 'platform_admin'].includes(normalizedRole);
}

function buildScopeGuidance(selectedLocationCode, locationLabel) {
  if (selectedLocationCode && selectedLocationCode !== 'ALL') {
    return `Scope is limited to ${locationLabel}. Governance counts and lists below reflect that selected location context.`;
  }

  return 'Scope is currently set to all available governance locations. Counts and lists below reflect the full visible governance workspace.';
}

function AdminWorkspaceHome({ onNavigate, summary, scopeGuidance }) {
  const cards = [
    {
      title: 'Users & Roles',
      copy: 'Manage role assignments, user access, and location-scoped governance settings.',
      tab: 'users',
      action: 'Open Users & Roles',
      value: summary.userCount,
      detail: summary.userCount === 1 ? '1 user in scope' : `${summary.userCount} users in scope`
    },
    {
      title: 'Location Ownership',
      copy: 'Assign and review primary Director ownership for each location.',
      tab: 'locations',
      action: 'Open Location Ownership',
      value: summary.locationCount,
      detail: summary.unassignedLocationCount > 0
        ? `${summary.unassignedLocationCount} without a primary owner`
        : 'All visible locations have a primary owner'
    },
    {
      title: 'Delegations',
      copy: 'Review active approval delegations and revoke temporary coverage when needed.',
      tab: 'delegations',
      action: 'Open Delegations',
      value: summary.activeDelegationCount,
      detail: summary.activeDelegationCount > 0
        ? `${summary.activeDelegationCount} active delegations in scope`
        : 'No active delegations right now'
    },
    {
      title: 'KPI Configuration',
      copy: 'Maintain KPI definitions and keep the governance model future-ready.',
      tab: 'kpis',
      action: 'Open KPI Configuration',
      value: summary.kpiCount,
      detail: summary.kpiCount > 0
        ? `${summary.kpiCount} KPI definitions loaded`
        : 'No KPI definitions available yet'
    },
    {
      title: 'Reporting & Export',
      copy: 'Review lightweight approval, delegation, and goal lifecycle reporting for the current governance scope.',
      tab: 'reporting',
      action: 'Open Reporting',
      value: summary.activeDelegationCount + summary.unassignedLocationCount,
      detail: 'Export approvals, delegations, and goal proposal rows from one governance workspace'
    }
  ];

  return (
    <>
      <section className="director-card admin-workspace-overview-card">
        <div className="role-section-header">
          <h2>Governance Overview</h2>
          <span>Workspace Summary</span>
        </div>
        <p className="role-section-copy">{scopeGuidance}</p>
        <div className="director-status-summary-grid admin-workspace-summary-grid">
          <div className="role-request-summary-card admin-workspace-summary-card">
            <span>Users</span>
            <strong>{summary.userCount}</strong>
          </div>
          <div className="role-request-summary-card admin-workspace-summary-card">
            <span>Locations</span>
            <strong>{summary.locationCount}</strong>
          </div>
          <div className="role-request-summary-card admin-workspace-summary-card">
            <span>No Primary Owner</span>
            <strong>{summary.unassignedLocationCount}</strong>
          </div>
          <div className="role-request-summary-card admin-workspace-summary-card">
            <span>Active Delegations</span>
            <strong>{summary.activeDelegationCount}</strong>
          </div>
          <div className="role-request-summary-card admin-workspace-summary-card">
            <span>KPIs</span>
            <strong>{summary.kpiCount}</strong>
          </div>
        </div>
      </section>

      <div className="director-secondary-grid admin-workspace-grid">
      {cards.map(card => (
        <section key={card.tab} className="director-card admin-workspace-card">
          <div className="role-section-header">
            <h2>{card.title}</h2>
            <span>Governance</span>
          </div>
          <p className="role-section-copy">{card.copy}</p>
          <div className="admin-workspace-card-metric">
            <strong>{card.value}</strong>
            <span>{card.detail}</span>
          </div>
          <div className="director-table-actions">
            <Button size="sm" onClick={() => onNavigate(card.tab)}>
              {card.action}
            </Button>
          </div>
        </section>
      ))}
      </div>
    </>
  );
}

function AdminSectionIntro({
  title,
  summary,
  guidance,
  emphasis = null
}) {
  return (
    <section className="director-card admin-section-intro">
      <div className="role-section-header">
        <h2>{title}</h2>
        <span>Governance Guidance</span>
      </div>
      <p className="role-section-copy">{summary}</p>
      <p className="director-muted">{guidance}</p>
      <StatusBanner kind="info" message={emphasis} className="role-inline-message" />
    </section>
  );
}

/**
 * Platform Workspace P1 — stable platform-admin identity gate.
 *
 * Identity vs. presentation:
 *   - identity: isPlatformWorkspaceUser(user) — does this user belong
 *     to the VizionOps platform tenant? Stable across acting-as
 *     because the server stamps a `isPlatformAdmin` boolean on the
 *     auth payload that survives the client-context overlay.
 *   - presentation: !user.viewingClientPortal — should we render the
 *     platform shell *right now*, or has the operator explicitly
 *     opened a client portal to act-as?
 *
 *   isPlatformAdmin && !viewingClientPortal → PlatformAdminDashboard
 *   viewingClientPortal === true             → ClientAdminDashboard
 *   otherwise (tenant admin)                 → ClientAdminDashboard
 */
export function isPlatformAdminUser(user) {
  return isPlatformWorkspaceUser(user) && !user?.viewingClientPortal;
}

function getCompanyStatusLabel(company) {
  if (company?.slug === PLATFORM_COMPANY_SLUG) {
    return 'Platform Owner';
  }

  const status = String(company?.status || '').toLowerCase();
  if (status === 'onboarding') return 'Onboarding';
  if (status === 'active') return company?.onboarding?.isComplete ? 'Active Client' : 'Active';
  if (status === 'suspended') return 'Suspended';
  if (status === 'archived') return 'Archived';
  return status || 'Unknown';
}

export function getLifecycleStatusMeta(company) {
  if (company?.slug === PLATFORM_COMPANY_SLUG) {
    return { label: 'Platform Owner', variant: 'primary' };
  }

  const status = String(company?.status || '').toLowerCase();
  const map = {
    onboarding: { label: 'Onboarding', variant: 'info' },
    active: { label: 'Active', variant: 'success' },
    suspended: { label: 'Suspended', variant: 'warning' },
    archived: { label: 'Archived', variant: 'neutral' }
  };
  return map[status] || { label: status || 'Unknown', variant: 'neutral' };
}

export function CompanyStatusChip({ company }) {
  const meta = getLifecycleStatusMeta(company);
  return <Pill variant={meta.variant}>{meta.label}</Pill>;
}

function isTrialEndingSoon(company) {
  const trialEndsAt = company?.billing?.trialEndsAt;
  if (!trialEndsAt) return false;
  const daysUntilEnd = (new Date(trialEndsAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24);
  return daysUntilEnd >= 0 && daysUntilEnd <= 7;
}

function buildCompanySlug(name) {
  return String(name || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

function getCompanyOnboardingProgress(company) {
  const counts = company?.counts || {};
  const steps = [
    {
      key: 'company-created',
      label: 'Company created',
      isComplete: Boolean(company?.id)
    },
    {
      key: 'admin-created',
      label: 'Admin user created',
      isComplete: Number(counts.users || 0) > 0
    },
    {
      key: 'locations-added',
      label: 'Locations added',
      isComplete: Number(counts.locations || 0) > 0
    },
    {
      key: 'kpis-configured',
      label: 'KPIs configured',
      isComplete: Number(counts.kpis || 0) > 0
    },
    {
      key: 'goals-entered',
      label: 'Goals entered',
      isComplete: Number(counts.goals || 0) > 0
    },
    {
      key: 'actuals-entered',
      label: 'Actuals entered',
      isComplete: Number(counts.actuals || 0) > 0
    },
    {
      key: 'dashboard-ready',
      label: 'Dashboard ready',
      isComplete: Boolean(company?.onboarding?.isComplete)
    }
  ];
  const completeCount = steps.filter(step => step.isComplete).length;
  const percent = Math.round((completeCount / steps.length) * 100);
  const nextStep = steps.find(step => !step.isComplete);

  return {
    steps,
    completeCount,
    percent,
    nextStepKey: nextStep?.key || null,
    nextStepLabel: nextStep?.label || 'Client portal is ready'
  };
}

function getCompanySetupAction(nextStepKey) {
  const actions = {
    'admin-created': {
      label: 'Add Admin User',
      mode: 'disabled',
      note: 'Coming soon'
    },
    'locations-added': {
      label: 'Enter Portal to Add Locations',
      mode: 'portal'
    },
    'kpis-configured': {
      label: 'Enter Portal to Configure KPIs',
      mode: 'portal'
    },
    'goals-entered': {
      label: 'Enter Portal to Add Goals',
      mode: 'portal'
    },
    'actuals-entered': {
      label: 'Enter Portal to Add Actuals',
      mode: 'portal'
    },
    'dashboard-ready': {
      label: 'Review Client Portal',
      mode: 'portal'
    }
  };

  return actions[nextStepKey] || null;
}

function getClientHealthScore(company) {
  const counts = company?.counts || {};
  const checks = [
    {
      key: 'company-created',
      points: 10,
      isComplete: Boolean(company?.id),
      nextAction: 'Create company record'
    },
    {
      key: 'admin-created',
      points: 15,
      isComplete: Number(counts.users || 0) > 0,
      nextAction: 'Create an admin user'
    },
    {
      key: 'locations-added',
      points: 15,
      isComplete: Number(counts.locations || 0) > 0,
      nextAction: 'Add locations'
    },
    {
      key: 'kpis-configured',
      points: 15,
      isComplete: Number(counts.kpis || 0) > 0,
      nextAction: 'Configure KPIs'
    },
    {
      key: 'goals-entered',
      points: 15,
      isComplete: Number(counts.goals || 0) > 0,
      nextAction: 'Enter goals'
    },
    {
      key: 'actuals-entered',
      points: 15,
      isComplete: Number(counts.actuals || 0) > 0,
      nextAction: 'Enter actuals'
    },
    {
      key: 'onboarding-complete',
      points: 15,
      isComplete: Boolean(company?.onboarding?.isComplete),
      nextAction: 'Mark dashboard ready'
    }
  ];
  const score = checks.reduce((sum, check) => sum + (check.isComplete ? check.points : 0), 0);
  const nextCheck = checks.find(check => !check.isComplete);
  const label = score >= 80 ? 'Healthy' : score >= 50 ? 'In Progress' : 'Needs Setup';
  const tone = score >= 80 ? 'healthy' : score >= 50 ? 'progress' : 'setup';

  return {
    score,
    label,
    tone,
    nextAction: nextCheck?.nextAction || 'Monitor client portal'
  };
}

function ClientHealthSummary({ company, compact = false }) {
  if (!company || company.slug === PLATFORM_COMPANY_SLUG) {
    return <span className="platform-owner-note">Platform owner</span>;
  }

  const health = getClientHealthScore(company);

  return (
    <div className={`platform-client-health${compact ? ' compact' : ''}`}>
      <div className="platform-client-health-score">
        <strong>{health.score}%</strong>
        <span className={`platform-client-health-pill ${health.tone}`}>{health.label}</span>
      </div>
      <p>{health.nextAction}</p>
    </div>
  );
}

function PlatformWidgetErrorBoundary({ children, resetKey, title }) {
  return (
    <DashboardErrorBoundary
      resetKey={resetKey}
      title={title}
      message="This widget could not load. The rest of the platform workspace is still available."
    >
      {children}
    </DashboardErrorBoundary>
  );
}

function ClientHealthWidget({ company }) {
  const clientHealth = getClientHealthScore(company);

  return (
    <section className="platform-client-health-card" aria-label="Client health score">
      <div>
        <span>Client Health</span>
        <strong>{clientHealth.score}%</strong>
      </div>
      <span className={`platform-client-health-pill ${clientHealth.tone}`}>{clientHealth.label}</span>
      <p>Next recommended action: {clientHealth.nextAction}</p>
    </section>
  );
}

function OnboardingTrackerWidget({ company, onEnterClientPortal }) {
  const onboardingProgress = getCompanyOnboardingProgress(company);
  const setupAction = getCompanySetupAction(onboardingProgress.nextStepKey);

  return (
    <section className="platform-onboarding-tracker" aria-label="Onboarding progress">
      <div className="platform-onboarding-tracker-header">
        <div>
          <span>Onboarding Tracker</span>
          <strong>{onboardingProgress.percent}% complete</strong>
        </div>
        <p>Next step: {onboardingProgress.nextStepLabel}</p>
      </div>
      {setupAction ? (
        <div className="platform-setup-action-row">
          <div>
            <span>Recommended Action</span>
            <strong>{setupAction.label}</strong>
            {setupAction.note ? <p>{setupAction.note}</p> : null}
          </div>
          <Button
            size="sm"
            disabled={setupAction.mode === 'disabled'}
            title={setupAction.note || undefined}
            onClick={() => {
              if (setupAction.mode === 'portal') {
                onEnterClientPortal?.(company);
              }
            }}
          >
            {setupAction.label}
          </Button>
        </div>
      ) : null}
      <div className="platform-onboarding-progress-bar" aria-hidden="true">
        <span style={{ width: `${onboardingProgress.percent}%` }} />
      </div>
      <ol className="platform-onboarding-steps">
        {onboardingProgress.steps.map(step => (
          <li key={step.key} className={step.isComplete ? 'complete' : ''}>
            <span aria-hidden="true">{step.isComplete ? '✓' : ''}</span>
            <strong>{step.label}</strong>
          </li>
        ))}
      </ol>
    </section>
  );
}

function matchesCompanyFilter(company, filterKey) {
  if (filterKey === 'all') {
    return true;
  }

  if (company.slug === PLATFORM_COMPANY_SLUG) {
    return false;
  }

  if (filterKey === 'active') {
    return company.status === 'active' || company.status === 'onboarding' || Boolean(company.onboarding?.isComplete);
  }

  if (filterKey === 'onboarding') {
    return company.status === 'onboarding' || !company.onboarding?.isComplete;
  }

  const health = getClientHealthScore(company);
  if (filterKey === 'healthy') {
    return health.score >= 80;
  }

  if (filterKey === 'progress') {
    return health.score >= 50 && health.score <= 79;
  }

  if (filterKey === 'setup') {
    return health.score <= 49;
  }

  return true;
}

function PlatformCompaniesTable({
  companies = [],
  selectedCompanySlug,
  onCreateCompany,
  onEnterClientPortal,
  onSelectCompany
}) {
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortKey, setSortKey] = useState('name');
  const [showArchived, setShowArchived] = useState(false);
  const normalizedSearch = searchTerm.trim().toLowerCase();
  const archivedHiddenCount = companies.filter(company => (
    company.slug !== PLATFORM_COMPANY_SLUG && company.status === 'archived'
  )).length;
  const visibleCompanies = companies.filter(company => showArchived || company.status !== 'archived');
  const filteredCompanies = visibleCompanies
    .filter(company => matchesCompanyFilter(company, activeFilter))
    .filter(company => {
      if (!normalizedSearch) {
        return true;
      }

      return String(company.name || '').toLowerCase().includes(normalizedSearch)
        || String(company.slug || '').toLowerCase().includes(normalizedSearch);
    })
    .sort((a, b) => {
      if (sortKey === 'health-desc' || sortKey === 'health-asc') {
        const delta = getClientHealthScore(b).score - getClientHealthScore(a).score;
        return sortKey === 'health-desc' ? delta : -delta;
      }

      if (sortKey === 'users-desc') {
        return Number(b.counts?.users || 0) - Number(a.counts?.users || 0);
      }

      if (sortKey === 'locations-desc') {
        return Number(b.counts?.locations || 0) - Number(a.counts?.locations || 0);
      }

      return String(a.name || '').localeCompare(String(b.name || ''), undefined, {
        sensitivity: 'base'
      });
    });

  return (
    <section className="director-card platform-company-table-card">
      <div className="role-section-header">
        <div>
          <h2>Companies</h2>
          <span>Company Directory</span>
        </div>
        <Button size="sm" onClick={onCreateCompany}>
          Add Company
        </Button>
      </div>
      <p className="role-section-copy">
        Company records are shown here without loading client KPI, goal, actual, alert, or rollup data into the VizionOps workspace.
      </p>
      <div className="platform-company-filter-row">
        <div className="platform-company-filter-chips" aria-label="Company filters">
          {COMPANY_FILTERS.map(filter => (
            <Button
              key={filter.key}
              size="sm"
              variant={activeFilter === filter.key ? 'primary' : 'secondary'}
              className="platform-company-filter-chip"
              onClick={() => setActiveFilter(filter.key)}
            >
              {filter.label}
            </Button>
          ))}
        </div>
        <span>Showing {filteredCompanies.length} of {visibleCompanies.length} companies</span>
      </div>
      <div className="platform-company-archive-row">
        <Button
          size="sm"
          variant={showArchived ? 'primary' : 'secondary'}
          onClick={() => setShowArchived(current => !current)}
        >
          {showArchived ? 'Hide archived' : 'Show archived'}
        </Button>
        {!showArchived && archivedHiddenCount > 0 ? (
          <span>{archivedHiddenCount} archived tenant{archivedHiddenCount === 1 ? '' : 's'} hidden.</span>
        ) : null}
      </div>
      <div className="platform-company-search-row">
        <label>
          Search
          <input
            type="search"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Company name or slug"
          />
        </label>
        <FormField label="Sort" htmlFor="company-sort-key">
          <Select
            id="company-sort-key"
            value={sortKey}
            onChange={(event) => setSortKey(event.target.value)}
            options={COMPANY_SORT_OPTIONS.map(option => ({
              value: option.key,
              label: option.label
            }))}
          />
        </FormField>
      </div>
      <div className="platform-company-table-wrap">
        <table className="platform-company-table">
          <thead>
            <tr>
              <th>Company</th>
              <th>Company status</th>
              <th>Plan</th>
              <th>Billing status</th>
              <th>Health</th>
              <th>Onboarding</th>
              <th>Users</th>
              <th>Active 30d</th>
              <th>Locations</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredCompanies.map((company) => (
              <tr key={company.id} className={selectedCompanySlug === company.slug ? 'selected' : ''}>
                <td>
                  <strong>{company.name}</strong>
                  <span>{company.slug}</span>
                </td>
                <td><CompanyStatusChip company={company} /></td>
                <td>{company.billing?.planTier || '—'}</td>
                <td>
                  {company.billing?.planStatus || '—'}
                  {isTrialEndingSoon(company) ? (
                    <Pill variant="warning" className="platform-trial-soon">Trial ending soon</Pill>
                  ) : null}
                </td>
                <td><ClientHealthSummary company={company} compact /></td>
                <td>{company.onboarding?.isComplete ? 'Complete' : 'In Progress'}</td>
                <td>{company.counts?.users || 0}</td>
                <td>{company.usage?.activeUsers30d || 0}</td>
                <td>{company.counts?.locations || 0}</td>
                <td>
                  <div className="director-table-actions">
                    <Button size="sm" variant="secondary" onClick={() => onSelectCompany(company)}>
                      View details
                    </Button>
                    {company.slug !== PLATFORM_COMPANY_SLUG ? (
                      <Button
                        size="sm"
                        onClick={() => onEnterClientPortal?.(company)}
                      >
                        {['suspended', 'archived'].includes(company.status) ? 'Open Read-only Client Portal' : 'Open Client Portal'}
                      </Button>
                    ) : null}
                  </div>
                </td>
              </tr>
            ))}
            {visibleCompanies.length === 0 ? (
              <tr>
                <td colSpan="10">No companies are available.</td>
              </tr>
            ) : null}
            {visibleCompanies.length > 0 && filteredCompanies.length === 0 ? (
              <tr>
                <td colSpan="10">No companies match this filter or search.</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function CreateCompanyModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: '',
    slug: '',
    initialAdminEmail: ''
  });
  const [slugTouched, setSlugTouched] = useState(false);

  const createCompany = useMutation(
    (payload) => apiRequest('/api/platform/companies', {
      method: 'POST',
      body: JSON.stringify(payload)
    }),
    {
      errorFallback: 'Unable to create company. Try again.',
      successMessage: 'Company created.'
    }
  );

  const handleChange = (field, value) => {
    setForm(current => {
      if (field === 'name' && !slugTouched) {
        return {
          ...current,
          name: value,
          slug: buildCompanySlug(value)
        };
      }

      return {
        ...current,
        [field]: field === 'slug' ? buildCompanySlug(value) : value
      };
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const result = await createCompany.mutate({
      name: form.name.trim(),
      slug: form.slug.trim(),
      status: 'onboarding',
      initialAdminEmail: form.initialAdminEmail.trim()
    });

    if (result?.company) {
      await onCreated?.(result.company);
      onClose?.();
    }
  };

  return (
    <DirectorModal title="Create Company" badge="New Tenant" onClose={onClose}>
      <p className="role-section-copy">
        Create the tenant record and start onboarding without loading or copying client operational data.
      </p>

      <form className="director-form-grid" onSubmit={handleSubmit}>
        <label className="director-form-span">
          Company Name
          <input
            value={form.name}
            onChange={(event) => handleChange('name', event.target.value)}
            required
            autoFocus
          />
        </label>

        <label>
          Slug
          <input
            value={form.slug}
            onChange={(event) => {
              setSlugTouched(true);
              handleChange('slug', event.target.value);
            }}
            required
          />
        </label>

        <label>
          Status
          <select value="onboarding" disabled>
            <option value="onboarding">Onboarding</option>
          </select>
        </label>

        <label className="director-form-span">
          Initial admin email
          <input
            type="email"
            value={form.initialAdminEmail}
            onChange={(event) => handleChange('initialAdminEmail', event.target.value)}
            placeholder="admin@example.com"
          />
        </label>

        <StatusBanner
          kind="info"
          message="Onboarding starts in progress. Initial admins are created with admin access and must use password recovery or an admin reset before signing in."
          className="role-inline-message director-form-span"
        />

        <StatusBanner
          kind="error"
          message={createCompany.error}
          className="role-inline-message director-form-span"
        />

        <div className="director-form-actions director-form-actions-spread director-form-span">
          <Button type="button" size="sm" variant="secondary" onClick={onClose}>
            Close
          </Button>
          <Button type="submit" size="sm" disabled={createCompany.isLoading}>
            {createCompany.isLoading ? 'Creating…' : 'Create Company'}
          </Button>
        </div>
      </form>
    </DirectorModal>
  );
}

const LIFECYCLE_ACTIONS = {
  suspend: {
    title: 'Suspend company',
    endpoint: (company) => `/api/platform/companies/${company.id}/suspend`,
    method: 'POST',
    buttonLabel: 'Suspend',
    variant: 'danger',
    requiresReason: true,
    requiresName: true,
    copy: 'This blocks all user logins immediately, revokes active sessions, pauses outbound work, and removes the company from active health metrics.'
  },
  archive: {
    title: 'Archive company',
    endpoint: (company) => `/api/platform/companies/${company.id}/archive`,
    method: 'POST',
    buttonLabel: 'Archive',
    variant: 'danger',
    requiresReason: true,
    requiresName: true,
    requiresCheckbox: true,
    copy: 'This hides the tenant from the default Companies view and excludes it from platform health metrics. Data is preserved.'
  },
  resume: {
    title: 'Resume company',
    endpoint: (company) => `/api/platform/companies/${company.id}/resume`,
    method: 'POST',
    buttonLabel: 'Resume',
    variant: 'primary',
    copy: 'This restores normal login access and returns the company to active operations.'
  },
  unarchive: {
    title: 'Un-archive company',
    endpoint: (company) => `/api/platform/companies/${company.id}/unarchive`,
    method: 'POST',
    buttonLabel: 'Un-archive',
    variant: 'secondary',
    copy: 'This moves the company back to suspended status. Logins remain blocked until it is resumed.'
  },
  markActive: {
    title: 'Mark company active',
    endpoint: (company) => `/api/platform/companies/${company.id}/mark-active`,
    method: 'POST',
    buttonLabel: 'Mark active',
    variant: 'primary',
    copy: 'This promotes the tenant from setup into an active operating workspace.'
  }
};

function LifecycleConfirmationModal({ actionKey, company, onClose, onComplete }) {
  const config = LIFECYCLE_ACTIONS[actionKey];
  const [reason, setReason] = useState(company?.notes || '');
  const [confirmName, setConfirmName] = useState('');
  const [understandsArchive, setUnderstandsArchive] = useState(false);
  const lifecycleAction = useMutation(
    (payload) => apiRequest(config.endpoint(company), {
      method: config.method,
      body: JSON.stringify(payload)
    }),
    {
      errorFallback: 'Unable to update company lifecycle status.',
      successMessage: 'Company lifecycle updated.'
    }
  );

  if (!config || !company) {
    return null;
  }

  const nameMatches = confirmName.trim().toLowerCase() === String(company.name || '').trim().toLowerCase();
  const canSubmit = (!config.requiresName || nameMatches)
    && (!config.requiresCheckbox || understandsArchive)
    && !lifecycleAction.isLoading;

  const handleSubmit = async (event) => {
    event.preventDefault();
    const result = await lifecycleAction.mutate({
      notes: config.requiresReason ? reason.trim() : undefined
    });
    if (result?.company) {
      await onComplete?.(result.company);
      onClose?.();
    }
  };

  return (
    <DirectorModal title={config.title} badge={company.name} onClose={onClose}>
      <form className="director-form-grid" onSubmit={handleSubmit}>
        <StatusBanner kind={actionKey === 'resume' || actionKey === 'markActive' ? 'info' : 'warning'} message={config.copy} className="role-inline-message director-form-span" />

        {config.requiresReason ? (
          <label className="director-form-span">
            Notes
            <textarea
              value={reason}
              maxLength={500}
              onChange={(event) => setReason(event.target.value)}
              placeholder="What's happening / who to contact"
            />
          </label>
        ) : null}

        {config.requiresName ? (
          <label className="director-form-span">
            Type the company name to confirm
            <input
              value={confirmName}
              onChange={(event) => setConfirmName(event.target.value)}
              placeholder={company.name}
              autoFocus
            />
          </label>
        ) : null}

        {config.requiresCheckbox ? (
          <label className="platform-lifecycle-checkbox director-form-span">
            <input
              type="checkbox"
              checked={understandsArchive}
              onChange={(event) => setUnderstandsArchive(event.target.checked)}
            />
            <span>I understand this tenant will be hidden and excluded from metrics.</span>
          </label>
        ) : null}

        <StatusBanner kind="error" message={lifecycleAction.error} className="role-inline-message director-form-span" />

        <div className="director-form-actions director-form-actions-spread director-form-span">
          <Button type="button" size="sm" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" size="sm" variant={config.variant} disabled={!canSubmit}>
            {lifecycleAction.isLoading ? 'Saving…' : config.buttonLabel}
          </Button>
        </div>
      </form>
    </DirectorModal>
  );
}

function LifecycleStatusBanner({ company, progress }) {
  const status = String(company?.status || '').toLowerCase();
  if (status === 'active') return null;

  if (status === 'onboarding') {
    return (
      <StatusBanner
        kind="info"
        message={`Setup in progress · ${progress.completeCount} of ${progress.steps.length} setup steps complete.`}
        className="role-inline-message"
      />
    );
  }

  if (status === 'suspended') {
    return (
      <StatusBanner
        kind="warning"
        message={`Suspended · logins blocked. ${company.notes ? `Notes: ${company.notes}` : 'No lifecycle notes yet.'}`}
        className="role-inline-message"
      />
    );
  }

  if (status === 'archived') {
    return (
      <StatusBanner
        kind="info"
        message={`Archived · hidden by default and excluded from active metrics. ${company.notes ? `Notes: ${company.notes}` : ''}`}
        className="role-inline-message"
      />
    );
  }

  return null;
}

function LifecycleActions({ company, onAction }) {
  const status = String(company?.status || '').toLowerCase();

  if (company?.slug === PLATFORM_COMPANY_SLUG) {
    return (
      <section className="platform-lifecycle-card">
        <div className="role-section-header">
          <h2>Lifecycle</h2>
          <span>Protected</span>
        </div>
        <p className="director-muted">Platform-owner workspace · status changes are not available.</p>
      </section>
    );
  }

  const actions = [];
  if (status === 'onboarding') actions.push('markActive');
  if (status === 'active') actions.push('suspend');
  if (status === 'suspended') actions.push('resume', 'archive');
  if (status === 'archived') actions.push('unarchive');

  return (
    <section className="platform-lifecycle-card">
      <div className="role-section-header">
        <h2>Lifecycle</h2>
        <span>Status Controls</span>
      </div>
      <div className="platform-lifecycle-actions">
        {actions.map(action => (
          <Button
            key={action}
            size="sm"
            variant={LIFECYCLE_ACTIONS[action].variant}
            onClick={() => onAction(action)}
          >
            {LIFECYCLE_ACTIONS[action].buttonLabel}
          </Button>
        ))}
      </div>
    </section>
  );
}

function CompanyManagementModal({ company, onClose, onEnterClientPortal, onCompanyUpdated }) {
  const [activeLifecycleAction, setActiveLifecycleAction] = useState('');
  const { data: usageSummary, isLoading: isLoadingUsage, error: usageError, refetch: refetchUsage } = useFetch(
    company ? `/api/platform/companies/${company.id}/usage-summary` : null,
    { initialData: null }
  );
  const billingMutation = useMutation(
    (payload) => apiRequest(`/api/platform/companies/${company?.id}/billing`, {
      method: 'PUT',
      body: JSON.stringify(payload)
    }),
    {
      errorFallback: 'Unable to save billing details.',
      successMessage: 'Billing details saved.'
    }
  );
  const [billingForm, setBillingForm] = useState({
    planTier: company?.billing?.planTier || '',
    planStatus: company?.billing?.planStatus || '',
    trialEndsAt: company?.billing?.trialEndsAt ? String(company.billing.trialEndsAt).slice(0, 10) : '',
    seatLimit: company?.billing?.seatLimit ?? '',
    billingContactEmail: company?.billing?.billingContactEmail || '',
    billingNotes: company?.billing?.billingNotes || ''
  });
  useEffect(() => {
    setBillingForm({
      planTier: company?.billing?.planTier || '',
      planStatus: company?.billing?.planStatus || '',
      trialEndsAt: company?.billing?.trialEndsAt ? String(company.billing.trialEndsAt).slice(0, 10) : '',
      seatLimit: company?.billing?.seatLimit ?? '',
      billingContactEmail: company?.billing?.billingContactEmail || '',
      billingNotes: company?.billing?.billingNotes || ''
    });
  }, [company]);

  if (!company) {
    return null;
  }

  const isPlatformOwner = company.slug === PLATFORM_COMPANY_SLUG;
  const onboardingStatus = company.onboarding?.isComplete ? 'Complete' : 'In Progress';
  const counts = company.counts || {};
  const onboardingProgress = getCompanyOnboardingProgress(company);
  const updateBillingField = (field, value) => setBillingForm((current) => ({ ...current, [field]: value }));
  const openClientPortal = (targetCompany = company, targetPath = '/executive') => {
    const companySlug = targetCompany?.companySlug || targetCompany?.slug || '';
    const companyName = targetCompany?.companyName || targetCompany?.name || '';
    const companyId = targetCompany?.companyId || targetCompany?.id || null;
    if (!companySlug || typeof onEnterClientPortal !== 'function') return;
    onEnterClientPortal({ companySlug, companyName, companyId, targetPath });
  };
  const saveBilling = async (event) => {
    event.preventDefault();
    const result = await billingMutation.mutate(billingForm);
    if (result?.company) {
      await onCompanyUpdated?.(result.company);
      await refetchUsage();
    }
  };

  return (
    <>
    <DirectorModal
      title={company.name}
      badge={getCompanyStatusLabel(company)}
      onClose={onClose}
      className="company-management-modal"
    >
      <div className="platform-company-management-stack">
      <LifecycleStatusBanner company={company} progress={onboardingProgress} />

      <div className="platform-company-detail-grid">
        <div>
          <span>Company name</span>
          <strong>{company.name}</strong>
        </div>
        <div>
          <span>Slug</span>
          <strong>{company.slug}</strong>
        </div>
        <div>
          <span>Status</span>
          <strong><CompanyStatusChip company={company} /></strong>
        </div>
        <div>
          <span>Onboarding</span>
          <strong>{onboardingStatus}</strong>
        </div>
        <div>
          <span>Users</span>
          <strong>{counts.users || 0}</strong>
        </div>
        <div>
          <span>Locations</span>
          <strong>{counts.locations || 0}</strong>
        </div>
      </div>

      {!isPlatformOwner ? (
        <>
          <PlatformWidgetErrorBoundary
            resetKey={`client-health:${company.id}:${company.updatedAt || company.status || ''}`}
            title="Client health score could not load."
          >
            <ClientHealthWidget company={company} />
          </PlatformWidgetErrorBoundary>

          <PlatformWidgetErrorBoundary
            resetKey={`onboarding-tracker:${company.id}:${company.updatedAt || company.status || ''}`}
            title="Onboarding tracker could not load."
          >
            <OnboardingTrackerWidget
              company={company}
              onEnterClientPortal={openClientPortal}
            />
          </PlatformWidgetErrorBoundary>
        </>
      ) : null}

      <StatusBanner
        kind={isPlatformOwner ? 'info' : 'warning'}
        message={isPlatformOwner
          ? 'Platform owner company'
          : 'This detail view uses company summary data only. Client KPIs, goals, actuals, alerts, and rollups remain inside the client portal.'}
        className="role-inline-message"
      />

      <LifecycleActions company={company} onAction={setActiveLifecycleAction} />

      <section className="platform-billing-card">
        <div className="role-section-header">
          <h2>Billing & Usage</h2>
          <span>Last 30 Days</span>
        </div>
        <form className="director-form-grid" onSubmit={saveBilling}>
          <label>
            Plan
            <input value={billingForm.planTier} onChange={(event) => updateBillingField('planTier', event.target.value)} />
          </label>
          <label>
            Status
            <input value={billingForm.planStatus} onChange={(event) => updateBillingField('planStatus', event.target.value)} />
          </label>
          <label>
            Trial ends
            <input type="date" value={billingForm.trialEndsAt} onChange={(event) => updateBillingField('trialEndsAt', event.target.value)} />
          </label>
          <label>
            Seat limit
            <input type="number" min="0" value={billingForm.seatLimit} onChange={(event) => updateBillingField('seatLimit', event.target.value)} />
          </label>
          <label className="director-form-span">
            Billing contact
            <input type="email" value={billingForm.billingContactEmail} onChange={(event) => updateBillingField('billingContactEmail', event.target.value)} />
          </label>
          <label className="director-form-span">
            Billing notes
            <textarea value={billingForm.billingNotes} onChange={(event) => updateBillingField('billingNotes', event.target.value)} />
          </label>
          <StatusBanner kind="error" message={billingMutation.error} className="role-inline-message director-form-span" />
          <div className="director-form-actions director-form-actions-spread director-form-span">
            <Button size="sm" type="submit" disabled={billingMutation.isLoading}>
              {billingMutation.isLoading ? 'Saving…' : 'Save billing'}
            </Button>
          </div>
        </form>
        <DirectorPanelState kind="loading" message={isLoadingUsage ? 'Loading usage…' : ''} />
        <DirectorPanelState kind="error" message={usageError} />
        {usageSummary ? (
          <div className="platform-usage-summary-grid">
            <div><span>Active users</span><strong>{usageSummary.activeUsers}</strong></div>
            <div><span>Goal saves</span><strong>{usageSummary.goalSaves}</strong></div>
            <div><span>Actuals recorded</span><strong>{usageSummary.actualsRecorded}</strong></div>
            <div><span>Approvals reviewed</span><strong>{usageSummary.approvalsReviewed}</strong></div>
            <div><span>Coaching notes</span><strong>{usageSummary.coachingNotes}</strong></div>
            <div><span>Actions completed</span><strong>{usageSummary.coachingActionsCompleted}</strong></div>
          </div>
        ) : null}
      </section>

      {company?.id ? (
        <PlatformBillingTab
          companyId={company.id}
          companyName={company.name}
          data-testid="platform-billing-tab"
        />
      ) : null}

      {company?.id ? (
        <DigestConfigPanel
          companyId={company.id}
          companyName={company.name}
          data-testid="digest-config-panel"
        />
      ) : null}

      {company?.id ? (
        <section className="platform-company-settings-section" data-testid="company-settings-section">
          <div className="role-section-header">
            <h2>Company Settings</h2>
            <span>Platform-Owned Configuration</span>
          </div>
          <PlatformIntegrationsTab
            companyId={company.id}
            companyName={company.name}
            data-testid="platform-integrations-tab"
          />
          <PlatformAttendanceSettingsPanel
            companyId={company.id}
            companyName={company.name}
            data-testid="platform-attendance-settings-panel"
          />
        </section>
      ) : null}

      <div className="platform-company-actions">
        {!isPlatformOwner ? (
          <Button size="sm" onClick={openClientPortal}>
            {['suspended', 'archived'].includes(company.status) ? 'Enter Read-only Client Portal' : 'Enter Client Portal'}
          </Button>
        ) : null}
        <Button size="sm" variant="secondary" onClick={onClose}>
          Close
        </Button>
      </div>
      </div>
    </DirectorModal>
    {activeLifecycleAction ? (
      <LifecycleConfirmationModal
        actionKey={activeLifecycleAction}
        company={company}
        onClose={() => setActiveLifecycleAction('')}
        onComplete={onCompanyUpdated}
      />
    ) : null}
    </>
  );
}

function PlatformAdminDashboard({
  currentPath,
  onEnterClientPortal,
  user,
  userDisplayName = '',
  userReferenceTitle = '',
  workspaceLabel = 'Platform Admin Workspace'
}) {
  // P3 / diagnostic deep-link support: honor `?tab=` so support workflows
  // can hand out URLs like /admin-workspace?tab=billing and land on the
  // expected tab. Defaults to 'companies' so the platform admin lands on
  // the Company Directory directly (visible affordances for Add Company
  // + Open Client Portal). Legacy /admin-users path → users tab.
  const initialTab = (() => {
    if (currentPath === '/admin-users') return 'users';
    if (typeof window !== 'undefined') {
      const qs = new URLSearchParams(window.location?.search || '');
      const param = (qs.get('tab') || '').trim().toLowerCase();
      if (['home', 'companies', 'users', 'billing', 'tenant-config'].includes(param)) return param;
    }
    return 'companies';
  })();
  const [activeTab, setActiveTab] = useState(() => initialTab);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isCompanyDetailOpen, setIsCompanyDetailOpen] = useState(false);
  const [isCreateCompanyOpen, setIsCreateCompanyOpen] = useState(false);
  const { data, isLoading, error, refetch: refetchCompanies } = useFetch('/api/platform/companies', {
    initialData: { summary: {}, companies: [] }
  });

  const companies = Array.isArray(data?.companies) ? data.companies : [];
  const clientCompanies = companies.filter((company) => company.slug !== PLATFORM_COMPANY_SLUG);
  const platformCompany = companies.find((company) => company.slug === PLATFORM_COMPANY_SLUG) || null;
  const selected = selectedCompany || clientCompanies[0] || platformCompany;
  const visiblePlatformTabs = PLATFORM_TABS.filter((tab) => tab.key !== 'tenant-config' || canEditTenantConfiguration(user));
  const summary = {
    totalCompanies: Number(data?.summary?.totalCompanies ?? data?.summary?.companies ?? clientCompanies.length),
    activeClients: Number(data?.summary?.activeClients || 0),
    onboardingInProgress: Number(data?.summary?.onboardingInProgress || 0),
    platformUsers: Number(data?.summary?.platformUsers || platformCompany?.counts?.users || 0),
    totalClientUsers: Number(data?.summary?.totalClientUsers || 0),
    totalClientLocations: Number(data?.summary?.totalClientLocations || 0),
    subscriptionRevenue: data?.summary?.subscriptionRevenue
  };

  const cards = [
    {
      title: 'Total Companies',
      value: summary.totalCompanies,
      detail: 'Client companies, excluding VizionOps',
      tab: 'companies'
    },
    {
      title: 'Active Clients',
      value: summary.activeClients,
      detail: 'Customer portals currently active',
      tab: 'companies'
    },
    {
      title: 'Onboarding In Progress',
      value: summary.onboardingInProgress,
      detail: 'Client portals not fully launched',
      tab: 'companies'
    },
    {
      title: 'Platform Users',
      value: summary.platformUsers,
      detail: 'VizionOps workspace users',
      tab: 'users'
    },
    {
      title: 'Total Client Users',
      value: summary.totalClientUsers,
      detail: 'Users across client companies',
      tab: 'companies'
    },
    {
      title: 'Total Client Locations',
      value: summary.totalClientLocations,
      detail: 'Locations across client companies',
      tab: 'companies'
    }
  ];

  // Sprint 3B.2: PlatformAdminDashboard now wraps in WorkspacePage
  // with width="operational" so the platform-admin company table,
  // billing surface, and onboarding posture get the new 2200px
  // shell on large monitors. The role-hero anatomy maps to slots:
  //   eyebrow     → workspaceLabel badge
  //   title       → Platform Admin Workspace
  //   subtitle    → workspace description
  //   contextSlot → scope-chip row (Profile / Title / Workspace / Tenant)
  // The legacy .role-page + .platform-admin-page wrapper survives as
  // an inner element so every existing platform-admin-page CSS rule
  // continues to apply without a stylesheet rewrite.
  const platformHeaderEyebrow = (
    <span className="role-badge">{workspaceLabel}</span>
  );

  const platformHeaderContextChips = (
    <div className="role-context-row">
      <div className="role-scope-chip">
        <span>Profile</span>
        <strong>{userDisplayName || user?.email || 'Platform admin'}</strong>
      </div>
      {userReferenceTitle ? (
        <div className="role-scope-chip">
          <span>Title</span>
          <strong>{userReferenceTitle}</strong>
        </div>
      ) : null}
      <div className="role-scope-chip">
        <span>Your workspace</span>
        <strong>Platform Admin</strong>
      </div>
      <div className="role-scope-chip">
        <span>Tenant</span>
        <strong>VizionOps</strong>
      </div>
    </div>
  );

  return (
    <WorkspacePage
      data-workspace-id="admin-dashboard"
      data-admin-shell="platform"
      width="operational"
      eyebrow={platformHeaderEyebrow}
      title="Platform Admin Workspace"
      subtitle="Manage customer companies, onboarding posture, platform users, and business operations without loading client operational dashboards into the VizionOps tenant."
      contextSlot={platformHeaderContextChips}
    >
      <div className="role-page platform-admin-page">
        <div className="admin-tab-bar" role="tablist" aria-label="Platform admin sections">
        {visiblePlatformTabs.map(tab => (
          <Button
            key={tab.key}
            size="sm"
            variant="secondary"
            role="tab"
            aria-selected={activeTab === tab.key}
            className={`admin-tab-btn${activeTab === tab.key ? ' active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </Button>
        ))}
      </div>

      <DirectorPanelState kind="loading" message={isLoading ? 'Loading platform workspace...' : ''} />
      <DirectorPanelState kind="error" message={error} />

      {activeTab === 'home' && (
        <>
          <section className="director-card admin-workspace-overview-card">
            <div className="role-section-header">
              <h2>Platform Overview</h2>
              <span>Business Workspace</span>
            </div>
            <p className="role-section-copy">
              This workspace tracks VizionOps platform operations only. Client locations, KPIs, goals, actuals, alerts, and rollups stay inside each client company portal.
            </p>
            <div className="director-status-summary-grid admin-workspace-summary-grid">
              {cards.map((card) => (
                <button
                  type="button"
                  key={card.title}
                  className="role-request-summary-card admin-workspace-summary-card platform-summary-button"
                  onClick={() => setActiveTab(card.tab)}
                >
                  <span>{card.title}</span>
                  <strong>{card.value}</strong>
                  <em>{card.detail}</em>
                </button>
              ))}
            </div>
          </section>

          <div className="director-secondary-grid admin-workspace-grid">
            <section className="director-card admin-workspace-card">
              <div className="role-section-header">
                <h2>Selected Company</h2>
                <span>{selected ? getCompanyStatusLabel(selected) : 'None'}</span>
              </div>
              {selected ? (
                <>
                  <p className="role-section-copy">{selected.name} · {selected.slug}</p>
                  <div className="admin-workspace-card-metric">
                    <strong>{selected.counts?.users || 0}</strong>
                    <span>users in this company record</span>
                  </div>
                  <p className="director-muted">
                    Operational data remains isolated. Open the company portal to review client locations, goals, actuals, alerts, or executive rollups.
                  </p>
                  <div className="director-table-actions">
                    <Button size="sm" onClick={() => setActiveTab('companies')}>View Companies</Button>
                  </div>
                </>
              ) : (
                <p className="role-section-copy">No company selected.</p>
              )}
            </section>

            <section className="director-card admin-workspace-card">
              <div className="role-section-header">
                <h2>Billing Status</h2>
                <span>Manual Billing</span>
              </div>
              <p className="role-section-copy">
                Plan and billing details are tracked manually per company while VizionOps stays free of automated billing workflows.
              </p>
              <div className="admin-workspace-card-metric">
                <strong>{clientCompanies.filter((company) => company.billing?.planStatus).length}</strong>
                <span>clients with billing status recorded</span>
              </div>
            </section>
          </div>
        </>
      )}

      {activeTab === 'companies' && (
        <>
          <AdminSectionIntro
            title="Companies"
            summary="View customer tenant records and choose which client portal to open when support or implementation work requires it."
            guidance="Opening a client portal uses that client workspace context; this VizionOps view does not load client operational dashboards."
          />
          <PlatformCompaniesTable
            companies={companies}
            onCreateCompany={() => setIsCreateCompanyOpen(true)}
            onEnterClientPortal={(targetCompany) => {
              const companySlug = targetCompany?.companySlug || targetCompany?.slug || '';
              const companyName = targetCompany?.companyName || targetCompany?.name || '';
              const companyId = targetCompany?.companyId || targetCompany?.id || null;
              const targetPath = targetCompany?.targetPath || '/executive';
              if (!companySlug) return;
              onEnterClientPortal?.({ companySlug, companyName, companyId, targetPath });
            }}
            selectedCompanySlug={selected?.slug}
            onSelectCompany={(company) => {
              setSelectedCompany(company);
              setIsCompanyDetailOpen(true);
            }}
          />
          {isCompanyDetailOpen && selectedCompany ? (
            <CompanyManagementModal
              company={selectedCompany}
              onClose={() => setIsCompanyDetailOpen(false)}
              onEnterClientPortal={(targetCompany) => {
                const companySlug = targetCompany?.companySlug || targetCompany?.slug || '';
                const companyName = targetCompany?.companyName || targetCompany?.name || '';
                const companyId = targetCompany?.companyId || targetCompany?.id || null;
                const targetPath = targetCompany?.targetPath || '/executive';
                if (!companySlug) return;
                onEnterClientPortal?.({ companySlug, companyName, companyId, targetPath });
              }}
              onCompanyUpdated={async (company) => {
                setSelectedCompany(current => ({
                  ...(current || {}),
                  ...company,
                  counts: current?.counts || company.counts || {},
                  onboarding: current?.onboarding || company.onboarding || {}
                }));
                await refetchCompanies();
              }}
            />
          ) : null}
          {isCreateCompanyOpen ? (
            <CreateCompanyModal
              onClose={() => setIsCreateCompanyOpen(false)}
              onCreated={async (company) => {
                setSelectedCompany(company);
                await refetchCompanies();
              }}
            />
          ) : null}
        </>
      )}

      {activeTab === 'users' && (
        <>
          <AdminSectionIntro
            title="Platform Users"
            summary="Manage VizionOps platform access only. Client users remain inside their own company workspaces."
            guidance="This list is scoped to the VizionOps company by the authenticated session."
            emphasis={summary.platformUsers === 0 ? 'No platform users are visible.' : null}
          />
          <DirectorUsersPanel
            locationLabel="VizionOps"
            selectedLocationCode="ALL"
          />
        </>
      )}

      {activeTab === 'billing' && (
        <section className="director-card admin-section-intro">
          <div className="role-section-header">
            <h2>Subscription / Revenue</h2>
            <span>Manual Billing</span>
          </div>
          <p className="role-section-copy">
            Billing metadata is managed inside each company detail panel. This view stays intentionally lightweight until automated billing is in scope.
          </p>
          <StatusBanner kind="info" message="Open a company from the Companies tab to record plan, trial, seat, contact, and usage details." className="role-inline-message" />
        </section>
      )}

      {activeTab === 'tenant-config' && (
        canEditTenantConfiguration(user) ? (
        <>
          <AdminSectionIntro
            title="Tenant Configuration"
            summary="Manage customer-facing branding and terminology for a selected tenant without changing operational data or tenant architecture."
            guidance="Select a company, update branding or terminology, and use the live preview to confirm exactly how labels will appear before saving."
          />
          <section className="director-card admin-section-intro">
            <div className="role-section-header">
              <h2>Select Tenant</h2>
              <span>Platform Scope</span>
            </div>
            <p className="role-section-copy">
              Platform administrators can update any tenant configuration from this workspace.
            </p>
            <FormField
              label="Company"
              htmlFor="platform-tenant-configuration-company"
              hint="Choose the tenant whose branding and terminology you want to manage."
            >
              <Select
                id="platform-tenant-configuration-company"
                value={selected?.id || ''}
                options={companies.map((company) => ({
                  value: company.id,
                  label: company.name
                }))}
                onChange={(event) => {
                  const nextCompany = companies.find((company) => Number(company.id) === Number(event.target.value));
                  setSelectedCompany(nextCompany || null);
                }}
              />
            </FormField>
          </section>
          <TenantConfigurationPanel
            companyId={selected?.id || null}
            companyName={selected?.name || ''}
            endpoint={selected?.id ? `/api/platform/companies/${selected.id}/settings/tenant-configuration` : ''}
            canEdit={canEditTenantConfiguration(user)}
            canManageIndustryTemplates
            shouldRefreshProvider={Number(selected?.id) === Number(user?.companyId)}
          />
        </>
        ) : (
          <UnauthorizedPage onGoHome={() => setActiveTab('home')} />
        )
      )}
      </div>
    </WorkspacePage>
  );
}

function ClientAdminDashboard({
  currentPath,
  locationLabel = 'Company Rollup',
  onNavigate,
  selectedLocationCode = 'ALL',
  user,
  userDisplayName = '',
  userReferenceTitle = '',
  workspaceLabel = 'Governance Workspace'
}) {
  const [activeTab, setActiveTab] = useState(() => pathToTab(currentPath));
  const scopeGuidance = useMemo(
    () => buildScopeGuidance(selectedLocationCode, locationLabel),
    [locationLabel, selectedLocationCode]
  );

  const scopedQueryParams = useMemo(() => ({
    locationCode: selectedLocationCode && selectedLocationCode !== 'ALL' ? selectedLocationCode : null
  }), [selectedLocationCode]);

  const summaryUrls = useMemo(() => ({
    users: withQuery('/api/users', scopedQueryParams),
    locations: '/api/locations',
    delegations: withQuery('/api/delegations', scopedQueryParams),
    kpis: '/api/kpis?includeInactive=true'
  }), [scopedQueryParams]);

  const { data: summaryData, isLoading: isLoadingSummary, error: summaryError } = useMultiFetch(summaryUrls);
  const canEditConfig = canEditTenantConfiguration(user);
  const visibleAdminTabs = ADMIN_TABS.filter((tab) => tab.key !== 'tenant-config' || canEditConfig);

  const summary = useMemo(() => {
    const users = Array.isArray(summaryData?.users) ? summaryData.users : [];
    const locations = Array.isArray(summaryData?.locations) ? summaryData.locations : [];
    const delegations = Array.isArray(summaryData?.delegations) ? summaryData.delegations : [];
    const kpis = Array.isArray(summaryData?.kpis) ? summaryData.kpis : [];

    const scopedLocations = locations.filter((location) => (
      selectedLocationCode === 'ALL' || location.code === selectedLocationCode
    ));
    const activeDelegations = delegations.filter((delegation) => delegation.status === 'active');
    const activeKpis = kpis.filter((kpi) => kpi.isActive);

    return {
      userCount: users.length,
      locationCount: scopedLocations.length,
      unassignedLocationCount: scopedLocations.filter(location => !location.primaryDirectorOwnerId).length,
      activeDelegationCount: activeDelegations.length,
      kpiCount: activeKpis.length
    };
  }, [summaryData, selectedLocationCode]);

  // Sprint 3B.2: ClientAdminDashboard wraps in WorkspacePage with
  // width="operational" matching the platform sibling. Same slot
  // mapping; the role-hero scope chips move into the contextSlot
  // and the role-page wrapper is retained inside the WorkspacePage
  // children so existing governance-workspace CSS keeps applying.
  const clientHeaderEyebrow = (
    <span className="role-badge">{workspaceLabel}</span>
  );

  const clientHeaderContextChips = (
    <div className="role-context-row">
      <div className="role-scope-chip">
        <span>Profile</span>
        <strong>{userDisplayName || 'Authenticated user'}</strong>
      </div>
      {userReferenceTitle ? (
        <div className="role-scope-chip">
          <span>Title</span>
          <strong>{userReferenceTitle}</strong>
        </div>
      ) : null}
      <div className="role-scope-chip">
        <span>Your workspace</span>
        <strong>Governance</strong>
      </div>
      <div className="role-scope-chip">
        <span>Scope</span>
        <strong>{locationLabel || 'Company Rollup'}</strong>
      </div>
    </div>
  );

  return (
    <WorkspacePage
      data-workspace-id="admin-dashboard"
      data-admin-shell="client"
      width="operational"
      eyebrow={clientHeaderEyebrow}
      title="Governance Workspace"
      subtitle="Manage platform governance, role assignments, ownership structure, and KPI configuration without entering the operational command center."
      contextSlot={clientHeaderContextChips}
    >
      <div className="role-page">
        <div className="admin-tab-bar" role="tablist" aria-label="Governance workspace sections">
        {visibleAdminTabs.map(tab => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.key}
            className={`admin-tab-btn${activeTab === tab.key ? ' active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <DirectorPanelState kind="loading" message={isLoadingSummary ? 'Loading governance summary...' : ''} />
      <DirectorPanelState kind="error" message={summaryError} />

      {activeTab === 'home' && (
        <AdminWorkspaceHome
          onNavigate={setActiveTab}
          scopeGuidance={scopeGuidance}
          summary={summary}
        />
      )}

      {activeTab === 'users' && (
        <>
          <AdminSectionIntro
            title="Users & Roles"
            summary="Use this section to assign roles, manage account access, and keep each user aligned to the right governance or operational scope."
            guidance={scopeGuidance}
            emphasis={summary.userCount === 0 ? 'No user accounts are visible in the current scope.' : null}
          />
          <DirectorUsersPanel
            locationLabel={locationLabel}
            selectedLocationCode={selectedLocationCode}
          />
        </>
      )}

      {activeTab === 'locations' && (
        <>
          <AdminSectionIntro
            title="Location Ownership"
            summary="Use this section to keep location records current and make sure each governed location has the right primary owner assigned."
            guidance={scopeGuidance}
            emphasis={summary.unassignedLocationCount > 0
              ? `${summary.unassignedLocationCount} location${summary.unassignedLocationCount === 1 ? ' is' : 's are'} missing a primary owner in the current scope.`
              : 'All visible locations currently have a primary owner assigned.'}
          />
          <DirectorLocationsPanel
            locationLabel={locationLabel}
            onNavigate={onNavigate}
            selectedLocationCode={selectedLocationCode}
          />
        </>
      )}

      {activeTab === 'delegations' && (
        <>
          <AdminSectionIntro
            title="Delegations"
            summary="Use this section to monitor temporary approval coverage and make sure backup or reassigned permissions remain time-bounded and location-specific."
            guidance={scopeGuidance}
            emphasis={summary.activeDelegationCount > 0
              ? `${summary.activeDelegationCount} active delegation${summary.activeDelegationCount === 1 ? '' : 's'} currently affect this governance scope.`
              : 'No active delegations are currently affecting this governance scope.'}
          />
          <DirectorDelegationsPanel
            locationLabel={locationLabel}
            selectedLocationCode={selectedLocationCode}
          />
        </>
      )}

      {activeTab === 'kpis' && (
        <>
          <AdminSectionIntro
            title="KPI Definitions"
            summary="Use this section to maintain platform KPI definitions and keep the shared measurement structure stable across the organization."
            guidance="KPI structure is platform-governed. Location scope can still help you narrow the surrounding governance context while reviewing the current library."
            emphasis={summary.kpiCount === 0 ? 'No active KPI definitions are currently available.' : null}
          />
          <DirectorKpisPanel
            locationLabel={locationLabel}
            selectedLocationCode={selectedLocationCode}
          />
        </>
      )}

      {activeTab === 'reporting' && (
        <>
          <AdminSectionIntro
            title="Reporting & Export"
            summary="Use this section for lightweight governance reporting across approvals, delegations, and goal lifecycle activity without changing the underlying workflow system."
            guidance={scopeGuidance}
            emphasis="Exports reflect the current location scope and are intended for simple offline review, handoff, and audit-friendly analysis."
          />
          <AdminReportingPanel
            locationLabel={locationLabel}
            selectedLocationCode={selectedLocationCode}
          />
        </>
      )}

      {activeTab === 'tenant-config' && (
        canEditConfig ? (
        <>
          <AdminSectionIntro
            title="Tenant Configuration"
            summary="Manage branding and terminology for the current tenant so labels update cleanly across navigation and workforce surfaces."
            guidance="Changes save directly to the current tenant configuration and refresh tenant-aware labels immediately."
          />
          <TenantConfigurationPanel
            companyId={user?.companyId || null}
            companyName={user?.companyName || ''}
            endpoint="/api/tenant-configuration"
            canEdit={canEditConfig}
            shouldRefreshProvider
          />
        </>
        ) : (
          <UnauthorizedPage onGoHome={() => setActiveTab('home')} />
        )
      )}
      </div>
    </WorkspacePage>
  );
}

function AdminDashboard(props) {
  if (isPlatformAdminUser(props.user)) {
    return <PlatformAdminDashboard {...props} />;
  }

  return <ClientAdminDashboard {...props} />;
}

export default AdminDashboard;
